import { useEffect, useMemo, useState } from "react";
import { css } from "styled-system/css";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type ClientRow = { clientId: string | null; email: string | null; displayName: string; phone: string | null };

export type OdontogramClientPrefill = {
  clientId: string | null;
  displayName: string;
  email: string | null;
  phone: string | null;
};

export function NewOdontogramRecordModal({
  open,
  onClose,
  onCreated,
  prefill = null,
  onPrefillConsumed,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (recordId: string) => void;
  prefill?: OdontogramClientPrefill | null;
  onPrefillConsumed?: () => void;
}) {
  const { refresh } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- client autocomplete ---
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [clientSearch, setClientSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setError(null);
    setBusy(false);
    setClientSearch("");
    setShowDropdown(false);
    setSelectedClientId(null);
  }, [open]);

  // --- load CRM clients for autocomplete ---
  useEffect(() => {
    if (!open) return;
    void fetchWithSessionSync("/api/dashboard/clients", { credentials: "include" }, refresh)
      .then(async (r) => (r.ok ? ((await r.json()) as ClientRow[]) : []))
      .then(setClients)
      .catch(() => setClients([]));
  }, [open, refresh]);

  // --- consume prefill (llegada desde una cita/cliente sin historia clínica todavía) ---
  useEffect(() => {
    if (!open || !prefill) return;
    const parts = prefill.displayName.trim().split(/\s+/);
    setFirstName(parts[0] ?? "");
    setLastName(parts.slice(1).join(" "));
    setEmail(prefill.email ?? "");
    setPhone(prefill.phone ?? "");
    setSelectedClientId(prefill.clientId);
    onPrefillConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefill]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients.slice(0, 8);
    return clients
      .filter(
        (c) =>
          c.displayName.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.phone?.toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [clients, clientSearch]);

  function selectClient(c: ClientRow) {
    setSelectedClientId(c.clientId);
    const parts = c.displayName.trim().split(/\s+/);
    setFirstName(parts[0] ?? "");
    setLastName(parts.slice(1).join(" "));
    setEmail(c.email ?? "");
    setPhone(c.phone ?? "");
    setClientSearch("");
    setShowDropdown(false);
  }

  function clearClientSelection() {
    setSelectedClientId(null);
    setClientSearch("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
  }

  async function submit() {
    if (!firstName.trim()) {
      setError("El nombre es obligatorio.");
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError("Ingresá al menos un email o teléfono.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await fetchWithSessionSync(
        "/api/dashboard/odontogram/records",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(
            selectedClientId
              ? { clientId: selectedClientId }
              : {
                  newClient: {
                    firstName: firstName.trim(),
                    lastName: lastName.trim() || undefined,
                    email: email.trim() || undefined,
                    phone: phone.trim() || undefined,
                  },
                },
          ),
          credentials: "include",
        },
        refresh,
      );
      const data = (await r.json()) as { record?: { id: string }; error?: string };
      if (!r.ok) {
        setError(data.error ?? "Error al crear historia.");
        return;
      }
      if (data.record?.id) {
        onCreated(data.record.id);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Nueva historia clínica">
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        {/* Autocomplete */}
        <div className={css({ position: "relative" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Buscar paciente existente</span>
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => {
                setClientSearch(e.target.value);
                setShowDropdown(true);
                if (selectedClientId) setSelectedClientId(null);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              placeholder="Nombre, email o teléfono..."
              autoComplete="off"
              className={css({
                h: "10",
                px: "3",
                fontSize: "sm",
                color: "#0f172a",
                borderWidth: "1px",
                borderColor: "#dbe2ea",
                rounded: "lg",
                bg: "#f8fafc",
                width: "100%",
                _focus: { outline: "none", bg: "white", borderColor: "#4f46e5" },
              })}
            />
          </label>

          {showDropdown && (clientSearch.trim() || filteredClients.length > 0) && (
            <div
              className={css({
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                mt: "1",
                bg: "white",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                rounded: "lg",
                shadow: "md",
                zIndex: "50",
                overflow: "hidden",
                maxH: "48",
                overflowY: "auto",
              })}
            >
              {filteredClients.map((c) => (
                <button
                  key={c.clientId ?? c.email}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); selectClient(c); }}
                  className={css({
                    display: "flex",
                    flexDir: "column",
                    gap: "0.5",
                    w: "100%",
                    px: "3",
                    py: "2",
                    textAlign: "left",
                    cursor: "pointer",
                    _hover: { bg: "#f1f5f9" },
                  })}
                >
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>{c.displayName}</span>
                  <span className={css({ fontSize: "xs", color: "#64748b" })}>
                    {c.email}{c.phone ? ` · ${c.phone}` : ""}
                  </span>
                </button>
              ))}
              {filteredClients.length === 0 && clientSearch.trim() && (
                <p className={css({ px: "3", py: "2", fontSize: "sm", color: "#94a3b8" })}>
                  Sin resultados. Completá los campos abajo para el nuevo paciente.
                </p>
              )}
            </div>
          )}
        </div>

        {selectedClientId && (
          <div
            className={css({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "3",
              px: "3",
              py: "1.5",
              bg: "#eef2ff",
              rounded: "lg",
              borderWidth: "1px",
              borderColor: "#c7d2fe",
            })}
          >
            <span className={css({ fontSize: "xs", color: "#4338ca" })}>Paciente del CRM seleccionado</span>
            <button
              type="button"
              onClick={clearClientSelection}
              className={css({
                fontSize: "xs",
                fontWeight: "semibold",
                color: "#4338ca",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                textDecoration: "underline",
              })}
            >
              Quitar selección
            </button>
          </div>
        )}

        <div className={css({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3" })}>
          <div>
            <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
              Nombre <span className={css({ color: "#ef4444" })}>*</span>
            </label>
            <Input
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Nombre"
              disabled={!!selectedClientId}
            />
          </div>
          <div>
            <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
              Apellido
            </label>
            <Input
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Apellido"
              disabled={!!selectedClientId}
            />
          </div>
        </div>

        <div>
          <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
            Email
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="paciente@email.com"
            disabled={!!selectedClientId}
          />
        </div>

        <div>
          <label className={css({ display: "block", fontSize: "xs", fontWeight: "medium", color: "#374151", mb: "1.5" })}>
            Teléfono
          </label>
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+54 9 11 ..."
            disabled={!!selectedClientId}
          />
        </div>

        {error ? (
          <p className={css({ fontSize: "sm", color: "#ef4444" })}>{error}</p>
        ) : null}

        <div className={css({ display: "flex", justifyContent: "flex-end", gap: "2", mt: "2" })}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={busy}>
            {busy ? "Creando…" : "Crear historia"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
